import React, { useState, useEffect } from "react";
import {
    Box,
    Flex,
    Text,
    Icon,
    Divider,
    Stack,
    Input,
} from "@chakra-ui/react";
import { BadgeCheck, BadgeInfo, Calendar, PercentCircle } from "lucide-react";
import ReusableDrawer from "components/Drawers/ReusableDrawer";
import GradientButton from "components/Buttons/GradientButton";
import { useDispatch } from "react-redux";
import {
    updateCommissionAction,
    fetchCommissionsAction,
} from "state/actions/driverCommissionsActions";
import { useCustomNotification } from "components/Toasts/OpenNotification";
import { formatDate } from "utils/dateFormatter";

const ViewCommissions = ({ isOpen, onClose, commission, commissionType }) => {
    const dispatch = useDispatch();
    const notify = useCustomNotification();

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [localCommission, setLocalCommission] = useState(commission);
    const [formValues, setFormValues] = useState({
        commission_percentage: "",
        trigger_date: "",
    });

    useEffect(() => {
        if (commission) {
            console.log("Received commission:", commission); 
            setLocalCommission(commission);
            setFormValues({
                commission_percentage: commission.commission_percentage || "",
                trigger_date: commission.trigger_date?.slice(0, 16) || "",
            });
        }
    }, [commission]);

    const handleChange = (field, value) => {
        setFormValues((prev) => ({ ...prev, [field]: value }));
    };

const handleToggleEdit = async () => {
    if (!isEditing) {
        setIsEditing(true);
        return;
    }

    setLoading(true);
    try {
        const payload = {
            ...formValues,
            commission_type: commissionType,
        };

        const response = await dispatch(
            updateCommissionAction(commission?.original?.commission_id, payload)
        );

        if (response.success) {
            setLocalCommission((prev) => ({
                ...prev,
                ...formValues,
                updated_at: new Date().toISOString(),
            }));
            notify({
                type: "success",
                message: `${commissionType} Commission Updated`,
                description: `${commissionType} commission details have been successfully updated.`,
            });
            onClose();
            dispatch(fetchCommissionsAction());
        } else {
            throw new Error("Failed to update");
        }
    } catch (error) {
        notify({
            type: "error",
            message: "Update Failed",
            description: error.message,
        });
    } finally {
        setLoading(false);
        setIsEditing(false);
    }
};

    const handleCloseDrawer = () => {
        setIsEditing(false);
        onClose();
    };

    if (!localCommission) return null;

    return (
        <Flex direction="column" p={8} width="100%">
            <ReusableDrawer
                isOpen={isOpen}
                onClose={handleCloseDrawer}
                headerContent={
                    <Flex justifyContent="space-between" alignItems="center" width="100%">
                        <Text fontSize="lg" fontWeight="bold">View {commissionType} Commission</Text>  {/* Dynamic Header */}
                    </Flex>
                }
                bodyContent={
                    <Box mt={4}>
                        <Text fontSize="2xl" fontWeight="bold" mb={2}>
                            {commissionType} Commission {/* Dynamic Commission Type */}
                        </Text>

                        <Flex mt={2} alignItems="center">
                            <Icon as={Calendar} size={16} color="gray.500" mr={1} />
                            <Text fontSize="sm" color="gray.500" mr={4}>
                                Created At: {formatDate(localCommission.createdAt)}
                            </Text>
                            <Box>
                                <Text fontSize="2xl" fontWeight="bold" display="flex" alignItems="center">
                                    {commission.status === 'Active' && (
                                        <>
                                            <Icon as={BadgeCheck} size={12} color="green.500" stroke="white" ml={1} fill="green.500" />
                                            <Text color="green.500" fontWeight="xs" fontSize="13px"> Active</Text>
                                        </>
                                    )}
                                    {commission.status === 'Inactive' && (
                                        <>
                                            <Icon as={BadgeInfo} size={12} color="yellow.500" stroke="white" ml={1} fill="yellow.500" />
                                            <Text color="yellow.500" fontWeight="xs" fontSize="13px" display="flex" alignItems="center"> Inactive</Text>
                                        </>
                                    )}
                                </Text>
                            </Box>
                        </Flex>

                        <Divider my={6} />

                        <Flex direction="column" p={4}>
                            <Text fontSize="lg" fontWeight="bold" mb={2}>Details</Text>
                            <Stack spacing={4}>
                                <Flex alignItems="center" border="1px" borderColor="gray.300" borderRadius="md" p={2}>
                                    <Icon as={PercentCircle} mr={2} color="gray.400" />
                                    {isEditing ? (
                                        <Input
                                            size="sm"
                                            type="number"
                                            value={formValues.commission_percentage}
                                            onChange={(e) => handleChange("commission_percentage", e.target.value)}
                                            placeholder="Commission %"
                                        />
                                    ) : (
                                        <Text fontWeight="500" color="gray.500" fontSize="sm">
                                            Commission: {localCommission.commissionPercentage}%
                                        </Text>
                                    )}
                                </Flex>

                                <Flex alignItems="center" border="1px" borderColor="gray.300" borderRadius="md" p={2}>
                                    <Icon as={Calendar} mr={2} color="gray.400" />
                                    {isEditing ? (
                                        <Input
                                            size="sm"
                                            type="datetime-local"
                                            value={formValues.trigger_date}
                                            onChange={(e) => handleChange("trigger_date", e.target.value)}
                                        />
                                    ) : (
                                        <Text fontWeight="500" color="gray.500" fontSize="sm">
                                            Trigger Date: {formatDate(localCommission.triggerAt)}
                                        </Text>
                                    )}
                                </Flex>
                            </Stack>

                            <Flex justifyContent="flex-end" mt={6}>
                                <GradientButton
                                    height='40px'
                                    fontSize='sm'
                                    onClick={handleToggleEdit}
                                    isLoading={loading}
                                    size="lg"
                                >
                                    {isEditing ? "Save" : "Edit"}
                                </GradientButton>
                            </Flex>
                        </Flex>
                    </Box>
                }
            />
        </Flex>
    );
};

export default ViewCommissions;
